import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../services/api";
import {
  User,
  ProjectDetailResponse,
  UserWithProjectRole,
  DataRequest,
  Dataset,
  ProjectInvitation,
} from "../types/api";
import { AddMemberWidget } from "./AddMemberWidget";
import { UserTable } from "./UserTable";
import { ImportWizard } from "./ImportWizard";
import { ProjectDatasetsSplitView } from "./ProjectDatasetsSplitView";
import { ProjectRequestsPanel } from "./ProjectRequestsPanel";
import { AdminRequestsSplitView } from "./AdminRequestsSplitView";
import { ProjectSettings } from "./ProjectSettings";
import { roleLabel } from "../utils/roleLabel";
import { saveDatasetBlob } from "../utils/datasetDownload";

interface ProjectPageProps {
  projectId: number;
  user?: User | null;
  isPIAnywhere?: boolean;
}

type Tab = "overview" | "members" | "datasets" | "requests" | "settings";

export const ProjectPage: React.FC<ProjectPageProps> = ({
  projectId,
  user,
  isPIAnywhere,
}) => {
  const navigate = useNavigate();

  // Global role — used only for the "back to admin" nav hint, never for gating
  // per-project admin UI (that's canManageProject, computed by the server below).
  const isManagerRole = user?.role === "manager";
  const isAdminUser = isManagerRole || !!isPIAnywhere;

  // Project info
  const [projectTitle, setProjectTitle] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [isProjectActive, setIsProjectActive] = useState(true);
  const [projectArchivedAt, setProjectArchivedAt] = useState<string | null>(
    null,
  );
  const [projectCreatedAt, setProjectCreatedAt] = useState("");
  const [projectOrgId, setProjectOrgId] = useState(0);
  const [projectOwnerId, setProjectOwnerId] = useState<number | null>(null);

  // Members — one shape for every role, carrying each member's per-project role.
  const [members, setMembers] = useState<UserWithProjectRole[]>([]);
  const [invitations, setInvitations] = useState<ProjectInvitation[]>([]);

  // Other data
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [requests, setRequests] = useState<DataRequest[]>([]);
  const [datasets, setDatasets] = useState<Dataset[]>([]);

  // The viewer's relationship to THIS project, computed by the server. The UI never
  // re-derives project permissions from the global role.
  const [myProjectRole, setMyProjectRole] = useState("");
  const [canManageProject, setCanManageProject] = useState(false);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [actionLoading, setActionLoading] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [downloadingDataset, setDownloadingDataset] = useState<number | null>(
    null,
  );
  const [uploadSuccessMsg, setUploadSuccessMsg] = useState<string | null>(null);
  // Datasets tab: the upload wizard takes over the tab while it's open.
  const [showUpload, setShowUpload] = useState(false);

  // Derived role flags — all per-project, driven by the server's response.
  const isProjectManager = myProjectRole === "manager"; // org manager viewing (oversight)
  // Archiving freezes the project: the server refuses every mutation in it, so no
  // management affordance may be offered until it is restored.
  const isArchived = !!projectArchivedAt;
  const canManage = canManageProject && !isProjectManager && !isArchived; // full PI management (add/remove members, upload)
  const canEditSettings = canManageProject; // PI of this project, or an org manager — Settings stays reachable so it can be restored

  useEffect(() => {
    loadData();
  }, [projectId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // One role-aware endpoint for everyone. The server returns the viewer's per-project
      // role + can_manage and the member list with per-project roles.
      const [detail, requestsData, datasetsData] = await Promise.all([
        apiService.getProjectDetail(
          projectId,
        ) as Promise<ProjectDetailResponse>,
        apiService.getProjectDataRequests(projectId),
        apiService.getDatasets(undefined, projectId),
      ]);

      setProjectTitle(detail.project.title);
      setProjectDescription(detail.project.description || "");
      setIsProjectActive(detail.project.is_active);
      setProjectArchivedAt(detail.project.archived_at ?? null);
      setProjectCreatedAt(detail.project.created_at);
      setProjectOrgId(detail.project.organization_id);
      setProjectOwnerId(detail.project.created_by_user_id ?? null);
      setMembers(detail.members || []);
      setMyProjectRole(detail.your_role || "");
      setCanManageProject(detail.can_manage);
      setRequests(requestsData.requests || []);
      setDatasets(datasetsData.datasets || []);

      // Only project managers need the full org user list (to add members) and
      // pending invitations — both are management-only information.
      if (detail.can_manage) {
        loadAllUsers();
        loadInvitations();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load project");
    } finally {
      setLoading(false);
    }
  };

  const loadAllUsers = async () => {
    try {
      const users = await apiService.adminGetUsers();
      setAllUsers(users || []);
    } catch {
      // non-critical
    }
  };

  const loadInvitations = async () => {
    try {
      const invitationsData = await apiService.listInvitations(projectId);
      setInvitations(invitationsData.invitations || []);
    } catch {
      // non-critical
    }
  };

  const downloadDataset = async (
    ds: Dataset,
    format: "native" | "csv" = "native",
  ) => {
    setDownloadingDataset(ds.id);
    try {
      const blob = await apiService.downloadDatasetPseudonymized(
        ds.id,
        projectId,
        ds.data_service_id ?? undefined,
        format,
      );
      saveDatasetBlob(blob, ds, format);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloadingDataset(null);
    }
  };

  const handleReviewRequest = async (
    requestId: number,
    status: "approved" | "rejected",
    notes: string,
  ) => {
    await apiService.reviewDataRequest(requestId, {
      status,
      admin_notes: notes,
    });
    await loadData();
  };

  const handleRemoveUser = async (userId: number, projectIdParam: number) => {
    try {
      setActionLoading(true);
      await apiService.adminRemoveUserFromProject(projectIdParam, userId);
      if (user && userId === user.id) {
        alert("You have been unenrolled from this project.");
        window.location.href = "/admin";
      } else {
        await loadData();
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to remove user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetArchived = async (archived: boolean) => {
    try {
      setArchiving(true);
      await apiService.adminSetProjectArchived(projectId, archived);
      await loadData();
    } catch (err) {
      alert(
        err instanceof Error ? err.message : "Failed to change archive state",
      );
    } finally {
      setArchiving(false);
    }
  };

  const handleDisable2FA = async (userId: number) => {
    try {
      setActionLoading(true);
      await apiService.adminDisableTwoFA(userId);
      await loadData();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to disable 2FA");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeInvite = async (invitationId: number) => {
    if (!window.confirm("Revoke this invitation?")) return;
    try {
      setActionLoading(true);
      await apiService.revokeInvitation(invitationId);
      await loadInvitations();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to revoke invitation");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopyInviteLink = (invitationId: number) => {
    const invitation = invitations.find((inv) => inv.id === invitationId);
    if (!invitation) return;
    const url = `${window.location.origin}/register?token=${invitation.token}`;
    navigator.clipboard.writeText(url);
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ink mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading project...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-red-800 font-semibold mb-2">
            Error Loading Project
          </h3>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  const memberCount = members.length;
  const activeInvitations = invitations.filter(
    (inv) => !inv.used_at && new Date(inv.expires_at) > new Date(),
  );
  const invitedRows = activeInvitations.map((inv) => ({
    id: inv.id,
    email: inv.email || "—",
    role: inv.role,
  }));
  const uploaderNameById: Record<number, string> = {};
  members.forEach((m) => {
    uploaderNameById[m.id] = m.name;
  });
  const datasetNames: Record<number, string> = {};
  datasets.forEach((ds) => {
    datasetNames[ds.id] = ds.display_name;
  });
  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: "overview", label: "Overview" },
    { id: "members", label: "Members", count: memberCount },
    { id: "datasets", label: "Datasets", count: datasets.length },
    { id: "requests", label: "Data Requests", count: requests.length },
  ];

  const pendingRequests = requests.filter((r) => r.status === "pending");
  const fulfilledCount = requests.filter(
    (r) => r.status === "fulfilled",
  ).length;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <style>{`
        .proj-back-link {
          font-size: 13px;
          color: var(--text-muted);
          margin: 0 0 8px;
          display: flex;
          align-items: center;
          gap: 4px;
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          font-family: inherit;
        }
        .proj-back-link:hover {
          color: var(--text-secondary);
        }
        .proj-header-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 4px;
          gap: 16px;
        }
        .proj-title {
          margin: 0;
          font-size: 24px;
          font-weight: 700;
          color: var(--text-primary);
          font-family: var(--font-display);
        }
        .proj-badges {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-shrink: 0;
        }
        .proj-archived-note {
          margin: 10px 0 0;
          padding: 9px 12px;
          border: 0.5px solid var(--border-strong);
          border-radius: var(--radius);
          background: var(--surface-1);
          font-size: 13px;
          line-height: 1.5;
          color: var(--text-secondary);
        }

        .proj-archived-note-link {
          background: none;
          border: none;
          padding: 0;
          font: inherit;
          color: var(--text-primary);
          text-decoration: underline;
          cursor: pointer;
        }

        .proj-desc {
          font-size: 14px;
          color: var(--text-secondary);
          margin: 0 0 16px;
        }
        .proj-meta-strip {
          display: flex;
          gap: 32px;
          padding: 14px 0;
          border-top: 0.5px solid var(--border);
          border-bottom: 0.5px solid var(--border);
          margin-bottom: 20px;
          flex-wrap: wrap;
        }
        .proj-meta-label {
          font-size: 11px;
          color: var(--text-muted);
          margin: 0 0 3px;
        }
        .proj-meta-value {
          font-size: 14px;
          font-weight: 500;
          margin: 0;
          color: var(--text-primary);
        }
        .proj-tabs {
          display: flex;
          gap: 24px;
          align-items: center;
          justify-content: space-between;
          border-bottom: 0.5px solid var(--border);
          margin-bottom: 20px;
          font-size: 14px;
        }
        .proj-tabs-left {
          display: flex;
          gap: 24px;
        }
        .proj-tab {
          padding: 0 0 10px;
          color: var(--text-secondary);
          background: none;
          border: none;
          border-bottom: 2px solid transparent;
          cursor: pointer;
          font-family: inherit;
          font-size: 14px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .proj-tab.active {
          color: var(--text-accent);
          border-bottom-color: var(--border-accent);
          font-weight: 500;
        }
        .proj-tab-count {
          background: var(--surface-1);
          border-radius: 10px;
          padding: 1px 7px;
          font-size: 12px;
          color: var(--text-secondary);
        }
        .proj-metric-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin-bottom: 20px;
        }
        .proj-metric-card {
          background: var(--surface-1);
          border-radius: var(--radius);
          padding: 1rem;
          text-align: left;
          cursor: pointer;
          border: 0.5px solid transparent;
        }
        .proj-metric-card:hover {
          border-color: var(--border-strong);
        }
        .proj-metric-label {
          font-size: 13px;
          color: var(--text-secondary);
          margin: 0 0 4px;
        }
        .proj-metric-number {
          font-size: 22px;
          font-weight: 500;
          margin: 0;
          color: var(--text-primary);
        }
        .proj-metric-sub {
          font-size: 13px;
          font-weight: 400;
          color: var(--text-muted);
        }
        .proj-section-label {
          font-size: 13px;
          font-weight: 500;
          color: var(--text-secondary);
          margin: 0 0 10px;
        }
        .proj-attention-zero {
          border: 0.5px solid var(--border);
          border-radius: var(--radius);
          padding: 11px 14px;
          display: flex;
          align-items: center;
          gap: 10px;
          color: var(--text-muted);
          font-size: 13px;
        }
        .proj-attention-list {
          border: 0.5px solid var(--border);
          border-radius: var(--radius);
          overflow: hidden;
        }
        .proj-attention-row {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 11px 14px;
          border-bottom: 0.5px solid var(--border);
        }
        .proj-attention-row:last-child {
          border-bottom: none;
        }
        .proj-attention-text {
          flex: 1;
          min-width: 0;
        }
        .proj-attention-title {
          font-size: 14px;
          font-weight: 500;
          margin: 0;
          color: var(--text-primary);
        }
        .proj-attention-sub {
          font-size: 12px;
          color: var(--text-muted);
          margin: 2px 0 0;
        }
        .proj-attention-review-btn {
          padding: 6px 12px;
          font-size: 13px;
          font-family: inherit;
          border-radius: var(--radius);
          border: 0.5px solid var(--border-strong);
          background: var(--surface-2);
          cursor: pointer;
          flex-shrink: 0;
        }
        .proj-attention-review-btn:hover {
          background: var(--surface-1);
        }
      `}</style>

      {/* Header */}
      <div className="mb-2">
        <button
          className="proj-back-link"
          onClick={() => navigate(isAdminUser ? "/admin/projects" : "/projects")}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to projects
        </button>
        <div className="proj-header-row">
          <h1 className="proj-title">{projectTitle}</h1>
          <div className="proj-badges">
            {isProjectManager && <span className="badge admin">View Only</span>}
            {projectArchivedAt ? (
              <span className="badge archived">Archived</span>
            ) : (
              <span
                className={`badge ${isProjectActive ? "active" : "inactive"}`}
              >
                {isProjectActive ? "Active" : "Inactive"}
              </span>
            )}
          </div>
        </div>
        {projectDescription && (
          <p className="proj-desc">{projectDescription}</p>
        )}
        {projectArchivedAt && (
          <p className="proj-archived-note">
            This project is inactive, therefore it is in a read-only state.
            {canEditSettings && (
              <>
                {" "}
                <button
                  type="button"
                  className="proj-archived-note-link"
                  onClick={() => setActiveTab("settings")}
                >
                  Restore it in Settings
                </button>
                .
              </>
            )}
          </p>
        )}
      </div>

      {/* Meta strip */}
      <div className="proj-meta-strip">
        <div>
          <p className="proj-meta-label">Created</p>
          <p className="proj-meta-value">{formatDate(projectCreatedAt)}</p>
        </div>
        {projectOrgId > 0 && (
          <div>
            <p className="proj-meta-label">Organization ID</p>
            <p className="proj-meta-value">{projectOrgId}</p>
          </div>
        )}
        {myProjectRole && (
          <div>
            <p className="proj-meta-label">Your role</p>
            <p className="proj-meta-value">
              {isProjectManager
                ? "Manager (view only)"
                : roleLabel(myProjectRole)}
            </p>
          </div>
        )}
      </div>

      {/* Tabs */}
      <nav className="proj-tabs">
        <div className="proj-tabs-left">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`proj-tab ${activeTab === tab.id ? "active" : ""}`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="proj-tab-count">{tab.count}</span>
              )}
            </button>
          ))}
        </div>
        {canEditSettings && (
          <button
            onClick={() => setActiveTab("settings")}
            className={`proj-tab ${activeTab === "settings" ? "active" : ""}`}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Settings
          </button>
        )}
      </nav>

      {/* ── OVERVIEW TAB ── */}
      {activeTab === "overview" && (
        <div>
          <div className="proj-metric-grid">
            <div
              className="proj-metric-card"
              onClick={() => setActiveTab("members")}
            >
              <p className="proj-metric-label">Team members</p>
              <p className="proj-metric-number">{memberCount}</p>
            </div>
            <div
              className="proj-metric-card"
              onClick={() => setActiveTab("datasets")}
            >
              <p className="proj-metric-label">Datasets</p>
              <p className="proj-metric-number">{datasets.length}</p>
            </div>
            <div
              className="proj-metric-card"
              onClick={() => setActiveTab("requests")}
            >
              <p className="proj-metric-label">Data requests</p>
              <p className="proj-metric-number">
                {requests.length}{" "}
                {requests.length > 0 && (
                  <span className="proj-metric-sub">
                    · {fulfilledCount} fulfilled
                  </span>
                )}
              </p>
            </div>
          </div>

          {canManageProject && (
            <div>
              <p className="proj-section-label">Needs your attention</p>
              {pendingRequests.length === 0 ? (
                <div className="proj-attention-zero">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--text-success)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="m9 12 2 2 4-4" />
                  </svg>
                  Nothing pending — all requests reviewed
                </div>
              ) : (
                <div className="proj-attention-list">
                  {pendingRequests.map((req) => (
                    <div className="proj-attention-row" key={req.id}>
                      <div className="proj-attention-text">
                        <p className="proj-attention-title">{req.title}</p>
                        <p className="proj-attention-sub">
                          {req.user_name ? `${req.user_name} · ` : ""}
                          Requested {formatDate(req.created_at)}
                        </p>
                      </div>
                      <button
                        className="proj-attention-review-btn"
                        onClick={() => navigate(`/admin/requests/${req.id}`)}
                      >
                        Review
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── MEMBERS TAB ── */}
      {activeTab === "members" && (
        <div>
          {/* Standalone owner note — the members table below hides the viewer's
              own row, so the owner would otherwise never see themselves marked
              as such when viewing their own project. */}
          {projectOwnerId != null && (
            <p
              style={{
                fontSize: "13px",
                color: "var(--text-muted)",
                margin: "0 0 16px",
              }}
            >
              Created by{" "}
              <strong style={{ color: "var(--text-secondary)" }}>
                {projectOwnerId === user?.id
                  ? "You"
                  : (members.find((m) => m.id === projectOwnerId)?.name ??
                    "a member no longer on this project")}
              </strong>
            </p>
          )}

          {/* PI: full management with AddMemberWidget */}
          {canManage && (
            <AddMemberWidget
              projectId={projectId}
              projectTitle={projectTitle}
              allUsers={allUsers}
              projectUsers={members}
              inviterName={user?.name || ""}
              inviterEmail={user?.email || ""}
              onUserAssigned={loadData}
            />
          )}

          {/* PI or manager: rich UserTable, with member-management actions only for a PI */}
          {canManageProject ? (
            members.length === 0 && invitedRows.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500">
                No members assigned to this project
              </div>
            ) : (
              <UserTable
                users={members}
                showIdColumn={false}
                showCreatedColumn={false}
                projectViewMode={true}
                currentProjectId={projectId}
                currentUserId={user?.id}
                projectOwnerId={projectOwnerId}
                onRemoveFromProject={canManage ? handleRemoveUser : undefined}
                onDisable2FA={canManage ? handleDisable2FA : undefined}
                actionLoading={actionLoading}
                showActions={canManage}
                invitedRows={invitedRows}
                onRevokeInvite={canManage ? handleRevokeInvite : undefined}
                onCopyInviteLink={canManage ? handleCopyInviteLink : undefined}
              />
            )
          ) : /* Researcher member: same flat table as the admin view, read-only. The
               server already redacts is_active/2FA state for non-managers. */
          members.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500">
              No members assigned to this project
            </div>
          ) : (
            <UserTable
              users={members}
              showIdColumn={false}
              showCreatedColumn={false}
              projectViewMode={true}
              currentUserId={user?.id}
              projectOwnerId={projectOwnerId}
              showActions={false}
            />
          )}
        </div>
      )}

      {/* ── DATASETS TAB ── */}
      {activeTab === "datasets" && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Project Datasets
              </h3>
              <p className="text-sm text-gray-600 mt-0.5">
                Datasets uploaded to or approved for this project
              </p>
            </div>
            <div className="flex gap-2">
              {!showUpload && (
                <>
                  <button onClick={loadData} className="btn btn-secondary">
                    Refresh
                  </button>
                  {canManage && (
                    <button
                      onClick={() => setShowUpload(true)}
                      className="btn btn-primary"
                    >
                      + Upload dataset
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* PI: admin-style upload flow. Shown on demand and on its own —
              leaving the existing dataset list on screen mid-upload made it
              unclear which columns and settings were being edited. */}
          {canManage && showUpload && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="font-medium text-gray-700 mb-1">
                    Upload Dataset to Project
                  </p>
                  <p className="text-sm text-gray-500">
                    Upload and de-identify data files for this project
                  </p>
                </div>
                <button
                  onClick={() => setShowUpload(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
              <ImportWizard
                projectId={projectId}
                currentUserId={user?.id || 0}
                defaultDownloadScope="project"
                uploadType="admin"
                inspectRoute={`/admin/projects/${projectId}/upload/inspect`}
                onUpload={async ({
                  file,
                  columns,
                  displayName,
                  metadataScope,
                  downloadScope,
                  downloadUserIds,
                  notes,
                  parseOptions,
                  piiFindingsAcknowledged,
                  piiValueRedactions,
                  piiDroppedColumns,
                }) => {
                  await apiService.adminUploadProjectFile(
                    projectId,
                    file,
                    columns,
                    notes,
                    metadataScope,
                    downloadScope,
                    downloadScope === "selected_members" ? downloadUserIds : [],
                    displayName || undefined,
                    piiFindingsAcknowledged ?? false,
                    piiValueRedactions ?? [],
                    piiDroppedColumns ?? [],
                    parseOptions,
                  );
                  setUploadSuccessMsg("File uploaded successfully.");
                  setShowUpload(false);
                  await loadData();
                }}
                onCancel={() => setShowUpload(false)}
              />
            </div>
          )}

          {uploadSuccessMsg && (
            <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm flex justify-between items-center">
              <span>{uploadSuccessMsg}</span>
              <button
                onClick={() => setUploadSuccessMsg(null)}
                className="ml-4 text-green-600 hover:text-green-800 font-bold"
              >
                ×
              </button>
            </div>
          )}

          {showUpload ? null : canManageProject ? (
            <ProjectDatasetsSplitView
              projectId={projectId}
              projectTitle={projectTitle}
              datasets={datasets}
              onDownload={downloadDataset}
              downloadingId={downloadingDataset}
              canManage={canManageProject}
              archived={isArchived}
              uploaderNameById={uploaderNameById}
            />
          ) : (
            <ProjectDatasetsSplitView
              projectId={projectId}
              projectTitle={projectTitle}
              datasets={datasets}
              onDownload={downloadDataset}
              downloadingId={downloadingDataset}
              archived={isArchived}
            />
          )}
        </div>
      )}

      {/* ── REQUESTS TAB ── */}
      {activeTab === "requests" && (
        <div>
          {canManageProject ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Project Data Requests
                </h3>
                {!isProjectManager && !isArchived && (
                  <button
                    onClick={() => {
                      navigate(`/admin/projects/${projectId}/new-request`, {
                        state: { projectTitle },
                      });
                    }}
                    className="px-4 py-2 bg-ink text-white rounded-lg hover:bg-ink-800 transition font-medium text-sm"
                  >
                    New Request
                  </button>
                )}
              </div>

              <AdminRequestsSplitView
                requests={requests}
                datasetNames={datasetNames}
                currentUserId={user?.id}
                currentUserRole={user?.role}
                onReview={handleReviewRequest}
                onDataUploaded={loadData}
                archived={isArchived}
              />
            </>
          ) : (
            <ProjectRequestsPanel
              projectId={projectId}
              requests={requests}
              currentUserId={user?.id}
            />
          )}
        </div>
      )}

      {/* ── SETTINGS TAB (PI/manager only) ── */}
      {activeTab === "settings" && canEditSettings && (
        <ProjectSettings
          projectId={projectId}
          initialTitle={projectTitle}
          initialDescription={projectDescription}
          onProjectUpdated={loadData}
          readOnly={isArchived}
          canLeave={canManage}
          isOnlyPI={members.filter((m) => m.project_role === "pi").length <= 1}
          leaving={actionLoading}
          onLeaveProject={() => user && handleRemoveUser(user.id, projectId)}
          canArchive={canEditSettings}
          isArchived={!!projectArchivedAt}
          archiving={archiving}
          onSetArchived={handleSetArchived}
        />
      )}

    </div>
  );
};
